/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { useLocation, NavLink as RouterNavLink } from 'react-router-dom';
import { Box, Center, Flex, Image, VStack, IconButton, HStack, useBreakpointValue, Drawer, Portal } from 'chakra-v3';
import { Link } from '../Link';
import { Text } from '../Text';
import { Button } from '../buttons/Index';
import { MenuItem } from './MenuItem';
import type { NavLink } from './MenuItem';
import { MdClose, MdLogout, MdMenuOpen, MdPerson } from 'react-icons/md';
import { GoBook } from 'react-icons/go';
import { FaRegFolder } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa';
import { RiMoneyRupeeCircleLine } from 'react-icons/ri';
import { withBaseUrl } from '../../utils/url';

const getMainNavLinks = (): NavLink[] => {
  const samikshaUrl = import.meta.env.VITE_SAMIKSHA_URL || '/samiksha';
  const isSamikshaExternal = samikshaUrl.startsWith('http://') || samikshaUrl.startsWith('https://');

  return [
    {
      label: 'Projects',
      path: '/dashboard',
      icon: <FaRegFolder size={24} color="currentColor" />,
    },
    {
      label: 'Payments',
      path: '/payments',
      icon: <RiMoneyRupeeCircleLine size={24} color="currentColor" />,
    },
    {
      label: 'Help',
      path: '/help',
      icon: <FaWhatsapp size={24} color="currentColor" />,
    },
    {
      label: 'Case Studies',
      path: samikshaUrl,
      icon: <GoBook size={24} color="currentColor" />,
      external: isSamikshaExternal,
    },
  ];
};

const userNavLinks: NavLink[] = [
  // Temporarily removed for demo
  {
    label: 'Profile',
    path: '/register',
    icon: <MdPerson size={24} color="currentColor" />,
  },
  {
    label: 'Logout',
    path: '/logout',
    icon: <MdLogout size={24} color="currentColor" />,
  },
];

// Combined for mobile menu - will be computed inside component

/**
 * Checks if a menu item path matches the current location.
 * Handles exact matches and path prefixes for nested routes.
 */
const isActivePath = (linkPath: string, currentPath: string): boolean => {
  // Exact match
  if (linkPath === currentPath) {
    return true;
  }
  // For paths like '/projects', also match '/projects/...' but not '/projects-something'
  if (currentPath.startsWith(linkPath + '/')) {
    return true;
  }
  return false;
};

// Responsive breakpoints
type UserHeaderProps = Record<string, never>;

/**
 * UserHeader component providing sticky, responsive navigation for authenticated users.
 *
 * Desktop (≥laptop/1024px): Shows logo on left and menu button on right. Clicking the menu button
 * expands all navigation links horizontally to the left of the button.
 * Tablet (tablet/768px-laptop/1023px): Shows hamburger menu for better space utilization.
 * Mobile (<tablet/768px): Shows hamburger menu that opens a slide-in panel.
 *
 * @remarks
 * Desktop navigation uses a horizontal expandable menu pattern that keeps the header
 * minimal by default while providing easy access to all navigation options.
 * Responsive breakpoints use theme breakpoints: mobile (480px), tablet (768px), laptop (1024px), xl (1280px).
 */
const UserHeader: React.FC<UserHeaderProps> = () => {
  const location = useLocation();
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
  const mainNavLinks = getMainNavLinks();
  const navLinks: NavLink[] = [...mainNavLinks, ...userNavLinks];

  const closeDesktopMenu = () => setIsDesktopMenuOpen(false);

  // Handle Escape key to close desktop menu
  useEffect(() => {
    if (!isDesktopMenuOpen || isMobile) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDesktopMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDesktopMenuOpen, isMobile]);

  return (
    <>
      <Box
        as="nav"
        bg="bg.primary"
        px={{ base: '16px', md: '32px', lg: '48px' }}
        py={{ base: '12px', md: '20px', lg: '24px' }}
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <Flex align="center" w="100%" gap={{ base: 2, md: 4 }} maxW="1920px" mx="auto" position="relative">
          {/* Logo */}
          <Link asChild variant="plain">
            <RouterNavLink to="/">
              <Flex
                as="span"
                cursor="pointer"
                _hover={{ opacity: 0.9 }}
                _active={{ opacity: 0.8 }}
                transition="opacity 0.2s ease"
                alignItems="center"
                bg="transparent"
                border="none"
                p={0}
                gap={{ base: '6px', md: '8px' }}
                flexShrink={0}
                minWidth={0}
                zIndex={1001}
              >
                <Center
                  w="120px"
                  h="36.219px"
                  flexShrink={0}
                  position="relative"
                  overflow="hidden"
                  aspectRatio="120.00/36.22"
                >
                  <Image
                    src={withBaseUrl('/karya_evals_logo.svg')}
                    alt="Karya Logo"
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    position="absolute"
                    inset={0}
                  />
                </Center>
              </Flex>
            </RouterNavLink>
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && isDesktopMenuOpen && (
            <HStack
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              gap={{ base: '16px', md: '18px', lg: '20px' }}
              align="center"
              zIndex={10}
            >
              <HStack gap={{ base: '16px', md: '18px', lg: '20px' }}>
                {mainNavLinks.map((link, index) => (
                  <React.Fragment key={link.path}>
                    {index > 0 && (
                      <Box w="0px" h="16px" flexShrink={0} position="relative">
                        <Box
                          position="absolute"
                          inset="0"
                          borderLeft="1px solid"
                          borderColor="text.primary"
                          opacity={0.3}
                        />
                      </Box>
                    )}
                    <MenuItem
                      link={link}
                      isActive={isActivePath(link.path, location.pathname)}
                      onClick={closeDesktopMenu}
                    />
                  </React.Fragment>
                ))}
              </HStack>
              <Box w="0px" h="16px" flexShrink={0} position="relative">
                <Box position="absolute" inset="0" borderLeft="1px solid" borderColor="text.primary" opacity={0.3} />
              </Box>
              <HStack gap={{ base: '16px', md: '18px', lg: '20px' }}>
                {userNavLinks.map((link, index) => (
                  <React.Fragment key={link.path}>
                    {index > 0 && (
                      <Box w="0px" h="16px" flexShrink={0} position="relative">
                        <Box
                          position="absolute"
                          inset="0"
                          borderLeft="1px solid"
                          borderColor="text.primary"
                          opacity={0.3}
                        />
                      </Box>
                    )}
                    <MenuItem
                      link={link}
                      isActive={isActivePath(link.path, location.pathname)}
                      onClick={closeDesktopMenu}
                    />
                  </React.Fragment>
                ))}
              </HStack>
            </HStack>
          )}

          {/* Menu Button - Desktop */}
          {!isMobile && (
            <Button
              variant="ghost"
              onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
              aria-label={isDesktopMenuOpen ? 'Close menu' : 'Open menu'}
              ml="auto"
              color="white"
            >
              {isDesktopMenuOpen ? (
                <MdClose size={24} color="white" />
              ) : (
                <HStack gap={2}>
                  <MdMenuOpen size={24} color="white" />
                  <span>Menu</span>
                </HStack>
              )}
            </Button>
          )}

          {/* Menu Button - Mobile manual placement since by default it placed at the top of the drawer*/}
          {isMobile && (
            <Drawer.Root placement="end">
              <Drawer.Trigger asChild>
                <Button variant="ghost" ml="auto" color="white">
                  <HStack gap={2}>
                    <MdMenuOpen size={24} color="white" />
                    <span>Menu</span>
                  </HStack>
                </Button>
              </Drawer.Trigger>
              <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                  <Drawer.Content bg="bg.primary" borderLeft="1px solid">
                    <Drawer.Header borderBottom="1px solid">
                      <Text size="lg" fontWeight="600" color="text.primary">
                        Menu
                      </Text>
                      <Drawer.CloseTrigger asChild>
                        <IconButton
                          aria-label="Close menu"
                          variant="ghost"
                          size="sm"
                          _hover={{ opacity: 0.8 }}
                          color="text.primary"
                          minW="40px"
                          h="40px"
                        >
                          <MdClose size={24} color="white" />
                        </IconButton>
                      </Drawer.CloseTrigger>
                    </Drawer.Header>
                    <Drawer.Body>
                      <Drawer.Context>
                        {(store) => (
                          <VStack gap={4} alignItems="stretch" pt={6}>
                            {navLinks.map((link) => (
                              <MenuItem
                                key={link.path}
                                link={link}
                                isActive={isActivePath(link.path, location.pathname)}
                                justifyContent="flex-start"
                                onClick={() => store.setOpen(false)}
                              />
                            ))}
                          </VStack>
                        )}
                      </Drawer.Context>
                    </Drawer.Body>
                  </Drawer.Content>
                </Drawer.Positioner>
              </Portal>
            </Drawer.Root>
          )}
        </Flex>
      </Box>
    </>
  );
};

export default UserHeader;
